import { FirebaseApp, getApp, initializeApp } from "firebase/app";
import {
  fetchSignInMethodsForEmail,
  getAuth,
  browserLocalPersistence,
  initializeAuth,
  signInAnonymously,
  Auth,
} from "firebase/auth";
import { AuthChangedEvent, FirebaseAuthEvents, RedirectEvent } from "./events";
import { EventEmitter, Handle } from "@greeter/event";
import { IAuthentication, SignInMethod, UserInfo } from "./IAuthentication";
import { logger, warner } from "@greeter/log";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { handleFirebase, mapSignInMethods } from "./utils";

const logId = "[Auth]" + "[NativeFirebaseAuth]";
const log = logger(logId);
const warn = warner(logId);

export class NativeFirebaseAuth implements IAuthentication {
  private emitter = new EventEmitter<FirebaseAuthEvents>();

  constructor(private firebaseConfig: any) {
    // TODO: Remove. Kept for now in case things break.
    // NOTE: This is temporary and is used to for current
    //       available login methods for email
    //       Otherwise we have to build backend to check
    //       what login methods are available for the email
    let app: FirebaseApp;
    try {
      log("Getting app");
      app = getApp();
    } catch {
      log("App not initialized. Initializing the app");
      app = initializeApp(firebaseConfig);
    }

    FirebaseAuthentication.addListener("authStateChange", async ({ user }) => {
      log(
        "Auth changed for native! Emitting auth state change to external listeners"
      );
      this.emitter.emit({
        event: "authStateChanged",
        data: await this.getUser(),
      });
    });
  }

  async updateEmail(email: string): Promise<UserInfo> {
    await FirebaseAuthentication.updateEmail({ newEmail: email });
    return await this.getUser();
  }

  async updateName(name: string): Promise<UserInfo> {
    await FirebaseAuthentication.updateProfile({ displayName: name });
    return await this.getUser();
  }

  private parseJwt(token: string): Record<string, unknown> {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  }

  async getUser(): Promise<UserInfo> {
    return await handleFirebase(async (): Promise<UserInfo> => {
      const user = await FirebaseAuthentication.getCurrentUser();
      // NOTE: FirebaseAuthentication.getIdTokenResult does NOT include custom claims.
      // we parse it ourselves to avout dealing with this.
      const token = await FirebaseAuthentication.getIdToken();
      const role = this.parseJwt(token.token)["role"] as UserInfo["role"];
      return {
        id: user.user?.uid ?? "",
        role: role ?? "VenueAdmin",
        name: user.user?.displayName ?? "",
        email: user.user?.email ?? "",
        isAnon: false,
      };
    });
  }

  async refreshToken(): Promise<string> {
    return (await FirebaseAuthentication.getIdToken({ forceRefresh: true }))
      .token;
  }

  onAuthReady(listener: (d: Date) => void): Handle {
    // We don't have a way to check on the native side. Simply trigger right away.
    listener(new Date());
    return () => {};
  }

  onRedirect(listener: (userInfo: UserInfo | undefined) => void): Handle {
    warn("Not available for native apps...");
    return this.emitter.on("redirect", (ev: RedirectEvent) => {
      listener(ev.data);
    });
  }

  async signInAsAnon(): Promise<void> {
    return await handleFirebase(async () => {
      warn("not signing in natively. Using web.");
      await signInAnonymously(getAuth());
    });
  }

  async fetchSignInMethods(email: string): Promise<SignInMethod[]> {
    return await handleFirebase(async () => {
      const methods = await fetchSignInMethodsForEmail(getAuth(), email);

      const signMethods: (SignInMethod | undefined)[] =
        methods.map(mapSignInMethods);

      return signMethods.filter((m) => !!m) as SignInMethod[];
    });
  }

  async createUserWithEmailAndPassword(
    email: string,
    password: string
  ): Promise<void> {
    return await handleFirebase(async () => {
      await FirebaseAuthentication.createUserWithEmailAndPassword({
        email,
        password,
      });
      await FirebaseAuthentication.signInWithEmailAndPassword({
        email,
        password,
      });
    });
  }

  async signInWithGoogle(): Promise<void> {
    return await handleFirebase(async () => {
      await FirebaseAuthentication.signInWithGoogle({
        scopes: ["profile", "email"],
      });
    });
  }

  async signInWithApple(): Promise<void> {
    return await handleFirebase(async () => {
      await FirebaseAuthentication.signInWithApple({
        scopes: ["name", "email"],
      });
    });
  }

  onAuthChange(listener: (user: UserInfo | undefined) => void): Handle {
    const l = (ev: AuthChangedEvent) => {
      listener(ev.data);
    };

    return this.emitter.on("authStateChanged", l);
  }

  async signInWithFacebook(): Promise<void> {
    return await handleFirebase(async () => {
      await FirebaseAuthentication.signInWithFacebook({
        scopes: ["public_profile", "email"],
      });
    });
  }

  async logout(): Promise<void> {
    return await handleFirebase(async () => {
      await FirebaseAuthentication.signOut();
    });
  }

  async getAuthToken(): Promise<string> {
    return await handleFirebase(async () => {
      return (await FirebaseAuthentication.getIdToken()).token;
    });
  }

  async signInWithEmailAndPassword(email: string, password: string) {
    return await handleFirebase(async () => {
      await FirebaseAuthentication.signInWithEmailAndPassword({
        email,
        password,
      });
    });
  }
}
